import { useForm } from "react-hook-form";
import { useEffect } from "react";

import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Check, Users, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { usePutData, useGetData } from "@/hooks/services/use-query-hooks";
import { JobPayload } from "@/hooks/services/pps/types/job-orders.type";
import { UpdateJobOrder } from "@/hooks/services/pps/handlers/job-orders-api";
import { GetEditableColumns } from "@/hooks/services/pps/handlers/editable-columns-api";

import {
  DetailField,
  DetailFieldGrid,
} from "../../../../components/detail-field";

import { useNavigate } from "@tanstack/react-router";
import { jobOrderStatusMap } from "../../utils/constants";
import { getVariantByStatus } from "../../utils/utils";

import {
  FormDatePickerV2,
  FormInput,
  FormSelect,
  FormTextarea,
  FormTimePicker,
} from "@/components/forms";
import { cn } from "@/lib/utils";
import { toastManager } from "@/lib/toast-manager";

// TODO: will setup centralize dayjs plugin
//
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);

// Editable columns label constants
const FIELD_LABELS = {
  PROD_REMARKS: "Prod Remarks",
  STATUS: "Status",
  START_DATE: "Strtmlfn",
  DUE_DATE: "Shop Edd1",
  MATERIAL_STATUS: "Material Status",
  MATERIAL_EDD: "Material Edd",
  PBSUBCONT_STATUS: "Pbsubcont Status",
  PBSUBCONT_EDD: "Pbsubcont Edd",
  STANDARD_HOURS: "QuotedFFP Lab Hr",
} as const;

const schema = z
  .object({
    prodRemarks: z.string().nullable(),
    statusId: z.string().nullable(),
    startDate: z
      .string()
      .nullable()
      .refine((val) => val !== null, {
        message: "Start date is required",
      }),
    dueDate: z
      .string()
      .nullable()
      .refine((val) => val !== null, {
        message: "Due date is required",
      }),
    materialStatus: z.string().nullable(),
    materialEdd: z
      .string()
      .nullable()
      .refine((val) => val !== null, {
        message: "Material Due date is required",
      }),
    pbsubcontStatus: z.string().nullable(),
    pbsubcontEdd: z
      .string()
      .nullable()
      .refine((val) => val !== null, {
        message: "pbSubcont Due date is required",
      }),
    standardHours: z.string().nullable(),
  })
  .superRefine((data, context) => {
    const isStartDateBefore = dayjs(data.startDate).isSameOrBefore(
      dayjs(data.dueDate),
    );

    if (!isStartDateBefore) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start date cannot be after due date",
        path: ["startDate"],
      });
    }

    return isStartDateBefore;
  });

type RegisterFormValues = z.infer<typeof schema>;

interface KanbanSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: any;
  withoutTechnician?: boolean;
  viewMode?: boolean;
  refetch?: () => void;
}

function SectionDivider({ title }: { title: string }) {
  return (
    <>
      <div className="aero:flex aero:items-center aero:gap-3 aero:w-full">
        <div className="aero:w-[3px] aero:h-6 aero:bg-brand-primary aero:rounded-full aero:flex-shrink-0" />
        <span className="aero:font-inter aero:font-bold aero:text-base aero:leading-6 aero:text-brand-primary aero:whitespace-nowrap">
          {title}
        </span>
      </div>
      <div className="aero:w-full aero:h-[1px] aero:bg-neutral-200" />{" "}
    </>
  );
}

function KanbanDrawerContent({
  open,
  onOpenChange,
  data,
  withoutTechnician = false,
  viewMode = false,
  refetch,
}: KanbanSheetProps) {
  const navigate = useNavigate();
  const { mutate, isPending } = usePutData<JobPayload, any>(UpdateJobOrder());

  const { data: editableColumnsData } = useGetData(GetEditableColumns(), {
    enabled: open,
  });

  const editableMap =
    editableColumnsData?.data?.reduce(
      (acc, col) => {
        acc[col.columnLabel] = col.properties.isEditable;
        return acc;
      },
      {} as Record<string, boolean>,
    ) || {};

  const isFieldEditable = (fieldLabel: string) => {
    return editableMap[fieldLabel] !== false;
  };

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(schema),
  });

  const { control, handleSubmit, reset } = form;

  useEffect(() => {
    if (open && data) {
      reset({
        prodRemarks: data?.prodRemarks || "",
        statusId:
          data?.status?.id?.toString() ||
          jobOrderStatusMap.not_started.id.toString(),
        startDate: data?.startDate
          ? dayjs(data.startDate).format("YYYY-MM-DD")
          : undefined,
        dueDate: data?.dueDate
          ? dayjs(data.dueDate).format("YYYY-MM-DD")
          : undefined,
        materialStatus: data?.materialStatus || "",
        materialEdd: data?.materialEdd
          ? dayjs(data.materialEdd).format("YYYY-MM-DD")
          : undefined,
        pbsubcontStatus: data?.pbsubcontStatus || "",
        pbsubcontEdd: data?.pbsubcontEdd
          ? dayjs(data.pbsubcontEdd).format("YYYY-MM-DD")
          : undefined,
        standardHours: data?.standardHours
          ? dayjs(dayjs(data?.standardHours, "HH:mm:ss").toDate()).format(
              "HH:mm:ss",
            )
          : undefined,
      });
    }
  }, [open, data, reset]);

  const handleSave = (formData: RegisterFormValues) => {
    // Build full payload with formatted dates
    const fullPayload: any = {
      prodRemarks: formData.prodRemarks,
      statusId: formData.statusId ? Number(formData.statusId) : undefined,
      startDate: dayjs(formData.startDate).format("YYYY-MM-DD"),
      dueDate: dayjs(formData.dueDate).format("YYYY-MM-DD"),
      materialStatus: formData.materialStatus,
      materialEdd:
        formData.materialEdd &&
        dayjs(formData.materialEdd).format("YYYY-MM-DD"),
      pbsubcontStatus: formData.pbsubcontStatus,
      pbsubcontEdd:
        formData.pbsubcontEdd &&
        dayjs(formData.pbsubcontEdd).format("YYYY-MM-DD"),
      standardHours: formData.standardHours,
    };

    // Filter payload based on editable columns
    // Map field names to their column labels
    const fieldToColumnMap: Record<string, string> = {
      prodRemarks: FIELD_LABELS.PROD_REMARKS,
      statusId: FIELD_LABELS.STATUS,
      startDate: FIELD_LABELS.START_DATE,
      dueDate: FIELD_LABELS.DUE_DATE,
      materialStatus: FIELD_LABELS.MATERIAL_STATUS,
      materialEdd: FIELD_LABELS.MATERIAL_EDD,
      pbsubcontStatus: FIELD_LABELS.PBSUBCONT_STATUS,
      pbsubcontEdd: FIELD_LABELS.PBSUBCONT_EDD,
      standardHours: FIELD_LABELS.STANDARD_HOURS,
    };

    // Filter out non-editable fields
    const filteredPayload: any = {};
    Object.keys(fullPayload).forEach((key) => {
      const columnLabel = fieldToColumnMap[key];
      if (!columnLabel || isFieldEditable(columnLabel)) {
        filteredPayload[key] = fullPayload[key];
      } else {
        console.log(
          `Field "${key}" (${columnLabel}) is not editable, excluding from payload`,
        );
      }
    });

    mutate(
      {
        jobId: "jobOrderId" in data ? data.jobOrderId : data.id,
        jobData: filteredPayload,
      } as unknown as JobPayload,
      {
        onSuccess: () => {
          toastManager.showSuccess(
            "Job Order Updated",
            `Job Order ${data.jobOrderNo} has been successfully updated.`,
          );

          onOpenChange(false);
          if (refetch) refetch();
        },
        onError: (error: any) => {
          toastManager.showError(
            "Update Failed",
            error?.message || "Failed to update the job order",
          );
        },
      },
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          "aero:sm:max-w-[600px] aero:p-0 aero:gap-0",
          "aero:flex aero:flex-col",
        )}
      >
        {/* Custom close button - hide default */}
        <style>{`
          [data-slot="sheet-content"] > button[class*="absolute"][class*="right-4"] {
            display: none;
          }
        `}</style>

        {/* Header */}
        <SheetHeader className="aero:p-6 aero:pb-0 aero:gap-3">
          <div className="aero:flex aero:items-center aero:justify-between aero:h-12 aero:gap-3">
            <SheetTitle className="aero:font-inter aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
              {data?.manufacturer || "Job Order Details"}
            </SheetTitle>
            <div className="aero:flex aero:items-center aero:gap-2">
              <Button
                intent="secondary"
                design="outline"
                type="button"
                size="md"
                className="aero:h-10 aero:gap-2 aero:px-4"
                onClick={() =>
                  navigate({
                    to: `/job-order/$id/assignment`,
                    params: {
                      id: "jobOrderId" in data ? data?.jobOrderId : data?.id,
                    },
                  })
                }
              >
                <Users className="aero:w-5 aero:h-5" />
                <span className="aero:font-bold aero:text-sm">Assignment</span>
              </Button>
              <SheetClose asChild>
                <button className="aero:w-6 aero:h-6 aero:flex aero:items-center aero:justify-center aero:rounded aero:hover:bg-neutral-100">
                  <X className="aero:w-5 aero:h-5 aero:text-neutral-500" />
                </button>
              </SheetClose>
            </div>
          </div>
          <DetailField
            label="Customer"
            value={data?.customer?.customerName || "-"}
          />
          {/* <p className="aero:text-sm aero:text-foreground">
            {data?.customer?.customerName || "-"}
          </p> */}
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={handleSubmit(handleSave)}
            className="aero:flex aero:flex-col aero:flex-1 aero:overflow-hidden"
          >
            <div className="aero:flex-1 aero:overflow-y-auto aero:px-6 aero:py-5">
              <div className="aero:flex aero:flex-col aero:gap-5">
                {/* Production Remarks Section */}
                <SectionDivider title="Production Remarks" />
                <div className="aero:flex aero:flex-col aero:gap-2">
                  {viewMode ? (
                    <div className="aero:p-3 aero:border aero:rounded-md aero:bg-muted/30">
                      <span className="aero:text-sm aero:text-foreground">
                        {data?.prodRemarks || "No production remarks available"}
                      </span>
                    </div>
                  ) : (
                    <FormTextarea
                      name="prodRemarks"
                      inputClassName="aero:h-[100px]"
                      control={control}
                      disabled={
                        isPending || !isFieldEditable(FIELD_LABELS.PROD_REMARKS)
                      }
                      placeholder="Enter production remarks..."
                      rows={4}
                    />
                  )}
                </div>

                {/* Basic Info Section */}
                <SectionDivider title="Basic Information" />
                <div className="aero:flex aero:flex-col aero:gap-4">
                  <DetailFieldGrid>
                    <DetailField
                      label="Job Order"
                      value={data?.jobOrderNo || "-"}
                    />
                    {viewMode ? (
                      <DetailField
                        label={FIELD_LABELS.STATUS}
                        value={
                          <Badge
                            variant={getVariantByStatus(
                              data?.status?.statusName,
                            )}
                          >
                            {data?.status?.statusName}
                          </Badge>
                        }
                      />
                    ) : (
                      <FormSelect
                        name="statusId"
                        label={FIELD_LABELS.STATUS}
                        placeholder="Select status"
                        triggerClassName="aero:w-full"
                        options={[
                          {
                            value: jobOrderStatusMap.not_started.id.toString(),
                            label: jobOrderStatusMap.not_started.name,
                          },
                          {
                            value: jobOrderStatusMap.wsi.id.toString(),
                            label: jobOrderStatusMap.wsi.name,
                          },
                          {
                            value: jobOrderStatusMap.in_progress.id.toString(),
                            label: jobOrderStatusMap.in_progress.name,
                          },
                          {
                            value:
                              jobOrderStatusMap.repair_in_progress.id.toString(),
                            label: jobOrderStatusMap.repair_in_progress.name,
                          },
                          {
                            value:
                              jobOrderStatusMap.evaluation_in_progress.id.toString(),
                            label:
                              jobOrderStatusMap.evaluation_in_progress.name,
                          },
                          {
                            value:
                              jobOrderStatusMap.pending_arc_generation.id.toString(),
                            label:
                              jobOrderStatusMap.pending_arc_generation.name,
                          },
                          {
                            value: jobOrderStatusMap.completed.id.toString(),
                            label: jobOrderStatusMap.completed.name,
                          },
                          {
                            value:
                              jobOrderStatusMap.pending_customer_approval.id.toString(),
                            label:
                              jobOrderStatusMap.pending_customer_approval.name,
                          },
                          {
                            value:
                              jobOrderStatusMap.pending_csd_quotation.id.toString(),
                            label: jobOrderStatusMap.pending_csd_quotation.name,
                          },
                          {
                            value:
                              jobOrderStatusMap.pending_subcon.id.toString(),
                            label: jobOrderStatusMap.pending_subcon.name,
                          },
                          {
                            value:
                              jobOrderStatusMap.pending_material.id.toString(),
                            label: jobOrderStatusMap.pending_material.name,
                          },
                        ]}
                        control={control}
                        disabled={
                          isPending || !isFieldEditable(FIELD_LABELS.STATUS)
                        }
                      />
                    )}
                  </DetailFieldGrid>
                  <DetailFieldGrid>
                    <DetailField
                      label="Part Number"
                      value={data?.part?.partNumber || "-"}
                    />
                    {/*    // </span> // <span className="aero:border aero:border-2 aero:rounded-md aero:text-sm aero:px-2 aero:py-1 aero:font-bold"> */}

                    <DetailField
                      label="Cost Centre"
                      value={data?.costCentre || "-"}
                    />
                  </DetailFieldGrid>
                </div>

                {/* Timeline Section */}
                <SectionDivider title="Timeline" />
                <div className="aero:flex aero:flex-col aero:gap-4">
                  {viewMode ? (
                    <>
                      <DetailFieldGrid>
                        <DetailField
                          label={FIELD_LABELS.START_DATE}
                          value={
                            data?.startDate
                              ? dayjs(data.startDate).format("MMMM D, YYYY")
                              : "-"
                          }
                        />
                        <DetailField
                          label={FIELD_LABELS.DUE_DATE}
                          value={
                            data?.dueDate
                              ? dayjs(data.dueDate).format("MMMM D, YYYY")
                              : "-"
                          }
                        />
                      </DetailFieldGrid>
                      <DetailFieldGrid>
                        <DetailField
                          label={FIELD_LABELS.STANDARD_HOURS}
                          value={data?.standardHours || "-"}
                        />
                      </DetailFieldGrid>
                    </>
                  ) : (
                    <>
                      <DetailFieldGrid>
                        <FormDatePickerV2
                          name="startDate"
                          label={FIELD_LABELS.START_DATE}
                          control={control}
                          disabled={
                            isPending ||
                            !isFieldEditable(FIELD_LABELS.START_DATE)
                          }
                        />
                        <FormDatePickerV2
                          name="dueDate"
                          label={FIELD_LABELS.DUE_DATE}
                          control={control}
                          disabled={
                            isPending || !isFieldEditable(FIELD_LABELS.DUE_DATE)
                          }
                        />
                      </DetailFieldGrid>
                      <DetailFieldGrid>
                        <FormTimePicker
                          name="standardHours"
                          label={FIELD_LABELS.STANDARD_HOURS}
                          control={control}
                          disabled={
                            isPending ||
                            !isFieldEditable(FIELD_LABELS.STANDARD_HOURS)
                          }
                        />
                      </DetailFieldGrid>
                    </>
                  )}
                </div>

                {/* Material & Subcontractor Section */}
                <SectionDivider title="Material & Subcontractor" />
                <div className="aero:flex aero:flex-col aero:gap-4">
                  {viewMode ? (
                    <>
                      <DetailFieldGrid>
                        <DetailField
                          label={FIELD_LABELS.MATERIAL_STATUS}
                          value={data?.materialStatus || "-"}
                        />
                        <DetailField
                          label={FIELD_LABELS.MATERIAL_EDD}
                          value={
                            data?.materialEdd
                              ? dayjs(data.materialEdd).format("MMMM D, YYYY")
                              : "-"
                          }
                        />
                      </DetailFieldGrid>
                      <DetailFieldGrid>
                        <DetailField
                          label={FIELD_LABELS.PBSUBCONT_STATUS}
                          value={data?.pbsubcontStatus || "-"}
                        />
                        <DetailField
                          label={FIELD_LABELS.PBSUBCONT_EDD}
                          value={
                            data?.pbsubcontEdd
                              ? dayjs(data.pbsubcontEdd).format("MMMM D, YYYY")
                              : "-"
                          }
                        />
                      </DetailFieldGrid>
                    </>
                  ) : (
                    <>
                      <DetailFieldGrid>
                        <FormInput
                          name="materialStatus"
                          label={FIELD_LABELS.MATERIAL_STATUS}
                          control={control}
                          disabled={
                            isPending ||
                            !isFieldEditable(FIELD_LABELS.MATERIAL_STATUS)
                          }
                        />
                        <FormDatePickerV2
                          name="materialEdd"
                          label={FIELD_LABELS.MATERIAL_EDD}
                          control={control}
                          disabled={
                            isPending ||
                            !isFieldEditable(FIELD_LABELS.MATERIAL_EDD)
                          }
                        />
                      </DetailFieldGrid>
                      <DetailFieldGrid>
                        <FormInput
                          name="pbsubcontStatus"
                          label={FIELD_LABELS.PBSUBCONT_STATUS}
                          control={control}
                          disabled={
                            isPending ||
                            !isFieldEditable(FIELD_LABELS.PBSUBCONT_STATUS)
                          }
                        />
                        <FormDatePickerV2
                          name="pbsubcontEdd"
                          label={FIELD_LABELS.PBSUBCONT_EDD}
                          control={control}
                          disabled={
                            isPending ||
                            !isFieldEditable(FIELD_LABELS.PBSUBCONT_EDD)
                          }
                        />
                      </DetailFieldGrid>
                    </>
                  )}
                </div>

                {/* Resources Section */}
                <SectionDivider title="Resources" />
                <div className="aero:flex aero:flex-col aero:gap-4">
                  {!withoutTechnician && (
                    <DetailField
                      label="Technicians"
                      value={
                        data?.ppsTechnicians?.length > 0 ? (
                          <div className="aero:flex aero:flex-col aero:gap-1">
                            {data.ppsTechnicians.map(
                              (tech: any, idx: number) => (
                                <span key={idx}>
                                  {tech.name} {tech.role && `(${tech.role})`}
                                </span>
                              ),
                            )}
                          </div>
                        ) : (
                          "Currently no technicians available"
                        )
                      }
                    />
                  )}
                  <DetailField
                    label="Workspace"
                    value={
                      data?.workspace?.workspaceName || "No workspace assigned"
                    }
                  />
                </div>

                {/* Summary Section */}
                <SectionDivider title="Summary" />
                <DetailFieldGrid>
                  <DetailField
                    label="Act Total Cost"
                    value={`$${data?.totalCost ? parseInt(data.totalCost).toLocaleString() : "0"}`}
                  />
                  <DetailField
                    label="SOSales"
                    value={`$${data?.components?.reduce((acc: number, c: any) => acc + (c.totalSales || 0), 0).toLocaleString() || "0"}`}
                  />
                </DetailFieldGrid>
              </div>
            </div>

            {/* Footer */}
            {!viewMode && (
              <SheetFooter className="aero:p-6 aero:pt-4 aero:border-t aero:border-neutral-200 aero:flex aero:flex-row aero:justify-end">
                <Button
                  type="submit"
                  intent="custom"
                  design="solid"
                  size="md"
                  onClick={handleSubmit(handleSave)}
                  className="aero:h-10 aero:gap-2 aero:px-4 aero:bg-brand-primary-700 aero:hover:bg-brand-primary-800 aero:text-white aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500"
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <span className="aero:animate-spin aero:w-4 aero:h-4 aero:border-2 aero:border-white aero:border-t-transparent aero:rounded-full" />
                      <span className="aero:font-bold aero:text-sm">
                        Saving...
                      </span>
                    </>
                  ) : (
                    <>
                      <Check className="aero:w-5 aero:h-5" />
                      <span className="aero:font-bold aero:text-sm">Save</span>
                    </>
                  )}
                </Button>
              </SheetFooter>
            )}
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}

export default KanbanDrawerContent;
